import React, { useState, useMemo, useCallback } from 'react';
import { Symbol } from '../../hooks/useSymbols';

type OutlinePanelProps = {
  symbols: Symbol[];
  onSelect: (symbol: Symbol) => void;
};

const kindOrder: Record<Symbol['kind'], number> = {
  class: 0,
  interface: 1,
  type: 2,
  enum: 3,
  function: 4,
  method: 5,
  property: 6,
  const: 7,
  let: 8,
  var: 9,
};

export const OutlinePanel = React.memo(function OutlinePanel({
  symbols,
  onSelect,
}: OutlinePanelProps) {
  const [collapsed, setCollapsed] = useState(false);
  const [sortBy, setSortBy] = useState<'position' | 'name' | 'kind'>('position');
  const [filter, setFilter] = useState('');

  const sortedSymbols = useMemo(() => {
    let filtered = symbols;

    // Apply filter
    if (filter) {
      const lowerFilter = filter.toLowerCase();
      filtered = symbols.filter(s => s.name.toLowerCase().includes(lowerFilter));
    }

    // Apply sorting
    return [...filtered].sort((a, b) => {
      switch (sortBy) {
        case 'name':
          return a.name.localeCompare(b.name);
        case 'kind':
          return (kindOrder[a.kind] ?? 99) - (kindOrder[b.kind] ?? 99);
        case 'position':
        default:
          return a.line - b.line;
      }
    });
  }, [symbols, sortBy, filter]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent, symbol: Symbol) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onSelect(symbol);
    }
  }, [onSelect]);

  const getKindColor = (kind: Symbol['kind']) => {
    switch (kind) {
      case 'function':
      case 'method':
        return 'var(--accent)';
      case 'class':
        return '#f59e0b';
      case 'interface':
        return '#22c55e';
      case 'type':
        return '#a855f7';
      case 'enum':
        return '#ef4444';
      default:
        return 'var(--muted)';
    }
  };

  return (
    <div className="outline-panel">
      <div
        className="outline-header"
        onClick={() => setCollapsed(!collapsed)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setCollapsed(!collapsed);
          }
        }}
      >
        <span className={`outline-collapse-icon ${collapsed ? '' : 'expanded'}`}>
          ▶
        </span>
        <span className="outline-title">OUTLINE</span>
        <span className="outline-count">{symbols.length}</span>
      </div>

      {!collapsed && (
        <>
          <div className="outline-toolbar">
            <input
              type="text"
              className="outline-filter"
              placeholder="Filter symbols..."
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              onClick={(e) => e.stopPropagation()}
            />
            <div className="outline-sort-buttons">
              <button
                className={`outline-sort-btn ${sortBy === 'position' ? 'active' : ''}`}
                onClick={() => setSortBy('position')}
                title="Sort by position"
              >
                #
              </button>
              <button
                className={`outline-sort-btn ${sortBy === 'name' ? 'active' : ''}`}
                onClick={() => setSortBy('name')}
                title="Sort by name"
              >
                A
              </button>
              <button
                className={`outline-sort-btn ${sortBy === 'kind' ? 'active' : ''}`}
                onClick={() => setSortBy('kind')}
                title="Sort by type"
              >
                T
              </button>
            </div>
          </div>

          <div className="outline-list">
            {sortedSymbols.length === 0 ? (
              <div className="outline-empty">
                {symbols.length === 0 ? 'No symbols found' : 'No matching symbols'}
              </div>
            ) : (
              sortedSymbols.map((symbol) => (
                <button
                  key={`${symbol.name}-${symbol.line}`}
                  className="outline-item"
                  onClick={() => onSelect(symbol)}
                  onKeyDown={(e) => handleKeyDown(e, symbol)}
                >
                  <span
                    className="outline-icon"
                    style={{ color: getKindColor(symbol.kind) }}
                  >
                    {symbol.icon}
                  </span>
                  <span className="outline-name">{symbol.name}</span>
                  <span className="outline-line">:{symbol.line}</span>
                </button>
              ))
            )}
          </div>
        </>
      )}
    </div>
  );
});
